import {err, ok} from './result';
import type {Result} from './result';

export type PageRequest<TCursor = string | null> = {
  readonly limit: number;
  readonly cursor: TCursor;
};

export type Page<T> = {
  readonly items: readonly T[];
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
};

export type CursorPaginationMeta = {
  nextCursor: string | null;
  hasMore: boolean;
};

export type OffsetPaginationMeta = {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  itemsPerPage: number;
};

export type PaginationMeta = CursorPaginationMeta | OffsetPaginationMeta;

export class InvalidCursorError extends Error {
  constructor(message = 'Invalid cursor.') {
    super(message);
    this.name = 'InvalidCursorError';
  }
}

const BASE64URL_ALPHABET = /^[A-Za-z0-9_-]*$/;

const bytesToBase64Url = (bytes: Uint8Array): string => {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '');
};

const base64UrlToBytes = (cursor: string): Result<Uint8Array, InvalidCursorError> => {
  if (!BASE64URL_ALPHABET.test(cursor)) {
    return err(new InvalidCursorError('Invalid cursor alphabet.'));
  }

  const padded =
    cursor.length % 4 === 0 ? cursor : `${cursor}${'='.repeat(4 - (cursor.length % 4))}`;
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');

  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return ok(bytes);
  } catch {
    return err(new InvalidCursorError('Invalid cursor encoding.'));
  }
};

export const encodeCursor = (payload: unknown): string => {
  const json = JSON.stringify(payload);
  return bytesToBase64Url(new TextEncoder().encode(json));
};

export const decodeCursor = (cursor: string): Result<unknown, InvalidCursorError> => {
  if (cursor.length === 0) {
    return err(new InvalidCursorError('Cursor must not be empty.'));
  }

  const bytesResult = base64UrlToBytes(cursor);
  if (!bytesResult.ok) {
    return bytesResult;
  }

  try {
    const json = new TextDecoder().decode(bytesResult.data);
    const parsed: unknown = JSON.parse(json);
    return ok(parsed);
  } catch {
    return err(new InvalidCursorError('Invalid cursor payload.'));
  }
};

export const createPage = <T>(
  rows: readonly T[],
  limit: number,
  toCursorPayload: (last: T) => unknown,
): Page<T> => {
  const hasMore = rows.length > limit;
  const items = hasMore ? rows.slice(0, limit) : rows;
  const lastItem = items[items.length - 1];
  const nextCursor =
    hasMore && lastItem !== undefined ? encodeCursor(toCursorPayload(lastItem)) : null;

  return {
    items,
    nextCursor,
    hasMore,
  };
};

export const toCursorMeta = <T>(page: Page<T>): CursorPaginationMeta => ({
  nextCursor: page.nextCursor,
  hasMore: page.hasMore,
});
